import { Tooltip } from "@common/index";
import clsx from "clsx";

import type { BrandConnectionAccessLevel } from "./brandPrototypeModel";

const ACCESS_LEVELS: BrandConnectionAccessLevel[] = [
  "no_access",
  "read",
  "action",
];

type ApplicationAccessPermissionControlProps = {
  /** Undefined represents a mixed value when this control applies in bulk. */
  value: BrandConnectionAccessLevel | undefined;
  onChange: (value: BrandConnectionAccessLevel) => void;
  accessLabels: Record<BrandConnectionAccessLevel, string>;
  /** The Project assignment may cap this app account at read-only. */
  availableAccessLevels?: BrandConnectionAccessLevel[];
  ariaLabel: string;
  canManageAccess: boolean;
  onCanManageAccessChange: () => void;
  manageAccessLabel: string;
  manageAccessHelp: string;
  disabled?: boolean;
  manageAccessDisabled?: boolean;
  manageAccessDisabledHelp?: string;
  /** Project member access only controls application use, never delegation. */
  showManageAccessControl?: boolean;
  density?: "default" | "compact";
  className?: string;
  accessControlClassName?: string;
  accessButtonClassName?: string;
};

/**
 * The permission controls shared by Brand access management and the invite
 * flow. Usage and access-management rights are deliberately independent.
 */
const ApplicationAccessPermissionControl = ({
  value,
  onChange,
  accessLabels,
  availableAccessLevels = ACCESS_LEVELS,
  ariaLabel,
  canManageAccess,
  onCanManageAccessChange,
  manageAccessLabel,
  manageAccessHelp,
  disabled = false,
  manageAccessDisabled = false,
  manageAccessDisabledHelp,
  showManageAccessControl = true,
  density = "default",
  className,
  accessControlClassName,
  accessButtonClassName,
}: ApplicationAccessPermissionControlProps) => {
  const manageAccessControl = (
    <span className="inline-flex items-center gap-2 border-l border-[#E8EBF3] pl-4">
      <span className="text-xs font-medium text-[#666F8D]">
        {manageAccessLabel}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={canManageAccess}
        aria-label={`${ariaLabel} ${manageAccessLabel}`}
        disabled={disabled || manageAccessDisabled}
        onClick={onCanManageAccessChange}
        className={clsx(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8D0E2] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70",
          canManageAccess ? "bg-[#2563EB]" : "bg-[#D7DBE5]",
        )}
      >
        <span
          className={clsx(
            "inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-150",
            canManageAccess ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
    </span>
  );

  return (
    <div className={clsx("flex flex-wrap items-center justify-end gap-4", className)}>
      <div
        className={clsx(
          "grid shrink-0 gap-1 rounded-[10px] border border-[#E1E6F0] bg-[#F4F5F8] p-1",
          !accessControlClassName && density === "compact"
            ? "grid-cols-[64px_64px_112px]"
            : !accessControlClassName && "grid-cols-[84px_76px_144px]",
          accessControlClassName,
        )}
        role="group"
        aria-label={ariaLabel}
      >
        {availableAccessLevels.map((accessLevel) => {
          const isSelected = value === accessLevel;
          return (
            <button
              key={accessLevel}
              type="button"
              disabled={disabled}
              onClick={() => onChange(accessLevel)}
              className={clsx(
                "min-w-0 whitespace-nowrap rounded-[7px] font-medium transition-[background-color,color,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-60",
                density === "compact"
                  ? "h-7 px-1.5 text-[11px]"
                  : "h-8 px-2 py-0 text-xs",
                accessButtonClassName,
                isSelected && "bg-[#1A162F] text-white shadow-sm",
                !isSelected &&
                  "text-[#667085] hover:bg-white/60 hover:text-[#1A162F]",
              )}
            >
              {accessLabels[accessLevel]}
            </button>
          );
        })}
      </div>
      {showManageAccessControl ? (
        <Tooltip
          content={
            manageAccessDisabled
              ? manageAccessDisabledHelp || manageAccessHelp
              : manageAccessHelp
          }
          side="top"
        >
          {manageAccessControl}
        </Tooltip>
      ) : null}
    </div>
  );
};

export default ApplicationAccessPermissionControl;
